// 跨視窗同步工具
import { logger } from './logger';

class CrossWindowSync {
  constructor() {
    this.syncKey = 'deepbuffett_auth_sync';
    this.statusKey = 'deepbuffett_auth_status';
    this.tabId = this.generateTabId();
    this.listeners = new Map();
    this.isInitialized = false;
  }

  // 生成唯一標籤ID
  generateTabId() {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // 初始化跨視窗同步
  init() {
    if (this.isInitialized) return;
    
    this.setupStorageListener();
    this.setupVisibilityChangeListener();
    this.setupBeforeUnloadListener();
    this.isInitialized = true;
    
    logger.log('跨視窗同步已初始化，標籤ID:', this.tabId);
  }

  // 設置 storage 事件監聽器
  setupStorageListener() {
    const handleStorageChange = (event) => {
      if (event.key === this.syncKey && event.newValue) {
        try {
          const data = JSON.parse(event.newValue);
          
          // 忽略來自同一標籤的事件
          if (data.tabId === this.tabId) return;
          
          logger.log('收到其他視窗的同步事件:', data);
          
          // 觸發對應的回調函數
          if (this.listeners.has(data.type)) {
            this.listeners.get(data.type).forEach(callback => {
              try {
                callback(data.payload);
              } catch (error) {
                logger.error('執行同步回調時出錯:', error);
              }
            });
          }
        } catch (error) {
          logger.error('解析同步數據失敗:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
  }

  // 設置頁面可見性變化監聽器
  setupVisibilityChangeListener() {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // 頁面變為可見時，檢查是否有其他視窗的更新
        this.checkForUpdates();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  // 設置頁面卸載監聽器
  setupBeforeUnloadListener() {
    const handleBeforeUnload = () => {
      // 頁面卸載時清理本地存儲
      this.cleanup();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
  }

  // 廣播事件給其他視窗
  broadcast(type, payload) {
    try {
      const data = {
        type,
        payload,
        tabId: this.tabId,
        timestamp: Date.now()
      };

      localStorage.setItem(this.syncKey, JSON.stringify(data));
      
      // 觸發 storage 事件
      window.dispatchEvent(new StorageEvent('storage', {
        key: this.syncKey,
        newValue: JSON.stringify(data),
        oldValue: localStorage.getItem(this.syncKey)
      }));

      logger.log('已廣播事件:', { type, tabId: this.tabId });
    } catch (error) {
      logger.error('廣播事件失敗:', error);
    }
  }

  // 廣播認證狀態
  broadcastAuthStatus(status) {
    this.broadcast('auth_status', status);
    
    // 同時保存到本地存儲
    try {
      const authData = {
        ...status,
        tabId: this.tabId,
        timestamp: Date.now()
      };
      
      // 如果是登入狀態，同時保存登入時間
      if (status.isAuthenticated && status.user) {
        localStorage.setItem('loginTime', Date.now().toString());
      } else if (!status.isAuthenticated) {
        // 如果是登出狀態，清除登入時間
        localStorage.removeItem('loginTime');
      }
      
      localStorage.setItem(this.statusKey, JSON.stringify(authData));
    } catch (error) {
      logger.error('保存認證狀態失敗:', error);
    }
  }

  // 檢查其他視窗的更新
  checkForUpdates() {
    try {
      const lastSync = localStorage.getItem(this.syncKey);
      if (lastSync) {
        const data = JSON.parse(lastSync);
        if (data.tabId !== this.tabId) {
          logger.log('發現其他視窗的更新:', data);
          
          if (this.listeners.has(data.type)) {
            this.listeners.get(data.type).forEach(callback => {
              try {
                callback(data.payload);
              } catch (error) {
                logger.error('執行更新回調時出錯:', error);
              }
            });
          }
        }
      }
    } catch (error) {
      logger.error('檢查更新失敗:', error);
    }
  }

  // 獲取當前認證狀態
  getCurrentAuthStatus() {
    try {
      const status = localStorage.getItem(this.statusKey);
      return status ? JSON.parse(status) : null;
    } catch (error) {
      logger.error('獲取認證狀態失敗:', error);
      return null;
    }
  }

  // 註冊事件監聽器
  on(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type).add(callback);
  }

  // 移除事件監聽器
  off(type, callback) {
    if (this.listeners.has(type)) {
      this.listeners.get(type).delete(callback);
    }
  }

  // 清理資源
  cleanup() {
    try {
      // 檢查是否還有其他標籤存在
      const currentStatus = this.getCurrentAuthStatus();
      if (currentStatus && currentStatus.tabId === this.tabId) {
        // 如果當前標籤是最後一個，清理認證狀態
        localStorage.removeItem(this.statusKey);
      }
    } catch (error) {
      logger.error('清理資源失敗:', error);
    }
  }

  // 獲取活躍標籤數量
  getActiveTabCount() {
    try {
      const status = this.getCurrentAuthStatus();
      if (!status) return 0;
      
      // 檢查狀態是否在5分鐘內更新過
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      return status.timestamp > fiveMinutesAgo ? 1 : 0;
    } catch (error) {
      logger.error('獲取活躍標籤數量失敗:', error);
      return 0;
    }
  }
}

// 創建單例實例
const crossWindowSync = new CrossWindowSync();

export default crossWindowSync; 